import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import toast from 'react-hot-toast';
import { c, t, radius } from '../../theme';
import AuthAlert, { humanAuthError } from '../../components/AuthAlert';
import logo from '../../assets/startup/static-logo.png';
import bgDesktop from '../../assets/login-bg-desktop.webp';
import bgMobile from '../../assets/login-bg-mobile.webp';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  // Stays on screen while the person fixes the field that caused it.
  const [formError, setFormError] = useState(null);
  // Where to go once the app actually knows we are signed in.
  const [pendingRoute, setPendingRoute] = useState(null);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('mv_remember_me_checked'));
  const [formData, setFormData] = useState({ email: '', password: '' });

  // THIS is the double-login fix.
  //
  // signInWithPassword resolving only means the token is in storage. It does
  // not mean AuthContext knows: `user` is React state, set by the
  // onAuthStateChange listener on a later tick, and that is what
  // ProtectedRoute reads. Navigating before then lands on a route that sees
  // user === null and bounces straight back here — which is why the first
  // attempt appeared to fail and the second worked.
  //
  // Polling supabase.auth.getSession() does not help: it reads the token that
  // is already in storage and returns instantly, waiting for nothing.
  //
  // So we record where we want to go, and let this effect do the navigating
  // once the context genuinely has the user and has finished loading.
  useEffect(() => {
    if (!pendingRoute) return;

    // A hard navigation, not a router push.
    //
    // Two earlier attempts tried to time this against React state — wait for
    // getSession(), then wait for AuthContext's `user` — and both still landed
    // on a route that had not yet agreed a session existed, so it bounced back
    // here. Hence "Welcome back" appearing, then nothing.
    //
    // Reloading removes the timing question completely. The token is already
    // in storage by now, so on the way back up AuthContext initialises from
    // getSession() with the session present from its very first render. There
    // is no window in which a guard can see a null user.
    //
    // The cost is a full app reload, roughly a second. The startup splash in
    // public/index.html covers it, and it is the same logo and rings as the
    // gate that is currently on screen, so it reads as one continuous wait.
    // Correctness over speed on the one screen that must never fail.
    window.location.replace(pendingRoute);
  }, [pendingRoute]);

  const handleChange = (e) => {
    if (formError) setFormError(null);
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const ensurePublicUserRow = async (authUser) => {
    const meta = authUser.user_metadata || {};
    const resolvedAccountType = meta.account_type === 'vendor' ? 'vendor' : 'customer';
    const resolvedRole = resolvedAccountType === 'vendor' ? 'vendor' : 'customer';
    const payload = {
      id: authUser.id, full_name: meta.full_name || authUser.email?.split('@')[0] || '',
      facility_name: resolvedAccountType === 'vendor' ? null : (meta.facility_name || null),
      facility_type: resolvedAccountType === 'vendor' ? null : (meta.facility_type || null),
      phone: meta.phone || null, address: meta.address || null, city: meta.city || null, state: meta.state || null,
      status: 'pending', customer_tier: 'normal', can_access_controlled: false, has_outstanding_debt: false,
      avatar_url: null, email: authUser.email || null, role: resolvedRole, account_type: resolvedAccountType,
      fixed_delivery_fee: 0, is_vendor_verified: false,
      vendor_status: resolvedAccountType === 'vendor' ? 'pending_verification' : 'inactive',
      whatsapp_number: resolvedAccountType === 'vendor' ? (meta.whatsapp_number || meta.phone || null) : null,
    };
    const { error } = await supabase.from('users').upsert([payload], { onConflict: 'id' });
    if (error) throw error;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) { setFormError({ title: 'Enter your email and password', detail: 'Both fields are required.' }); return; }
    setFormError(null);
    setLoading(true);
    setSigningIn(true);
    try {
      const email = formData.email.trim().toLowerCase();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password: formData.password });
      if (error) throw error;
      if (!data?.user?.id) throw new Error('Login failed');
      const userId = data.user.id;

      // Remember Me
      if (rememberMe) localStorage.setItem('mv_remember_me', '1');
      else localStorage.removeItem('mv_remember_me');
      localStorage.setItem('mv_remember_me_checked', rememberMe ? '1' : '');

      const { data: adminData, error: adminError } = await supabase.from('admin_users').select('*').eq('id', userId).maybeSingle();
      if (adminError) console.error('Admin lookup error:', adminError);
      if (adminData) { toast.success('Welcome back!'); setPendingRoute('/admin'); return; }

      let { data: userData, error: userError } = await supabase.from('users').select('*').eq('id', userId).maybeSingle();
      if (userError) throw userError;
      if (!userData) {
        await ensurePublicUserRow(data.user);
        const retry = await supabase.from('users').select('*').eq('id', userId).maybeSingle();
        if (retry.error) throw retry.error;
        userData = retry.data;
      }
      if (!userData) throw new Error('User profile not found');
      if (userData.status === 'suspended') { await supabase.auth.signOut(); setFormError({ title: 'Your account is suspended', detail: 'Contact support on +234 704 771 8468 to resolve this.' }); setSigningIn(false); return; }
      if (userData.status === 'blocked') { await supabase.auth.signOut(); setFormError({ title: 'Your account is blocked', detail: 'Contact support on +234 704 771 8468 to resolve this.' }); setSigningIn(false); return; }

      const isVendor = userData.account_type === 'vendor' || userData.role === 'vendor' || data.user.user_metadata?.account_type === 'vendor';
      if (isVendor) {
        if (userData.vendor_status === 'suspended') { await supabase.auth.signOut(); setFormError({ title: 'Your vendor account is suspended', detail: 'Contact support on +234 704 771 8468 to resolve this.' }); setSigningIn(false); return; }
        toast.success('Welcome back!'); setPendingRoute('/vendor'); return;
      }
      toast.success('Welcome back!'); setPendingRoute('/dashboard');
    } catch (error) { console.error('Login error:', error); setFormError(humanAuthError(error)); setSigningIn(false); setPendingRoute(null); }
    finally { setLoading(false); }
  };

  // Split layout on desktop: a brand panel carrying the pitch, and the form
  // beside it. Below 900px it collapses to a single column — the panel becomes
  // a compact header rather than disappearing, so the page still has an
  // identity on the phones most of these pharmacies actually use.
  const isWide = typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches;

  const labelStyle = { ...t.eyebrow, fontSize: '11px', marginBottom: '7px', display: 'block' };
  const inputStyle = { width: '100%', height: '52px', padding: '0 16px', background: c.surface, border: '1px solid ' + c.line, borderRadius: radius.md, color: c.ink, fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: t.family, transition: 'border-color .18s ease, box-shadow .18s ease' };
  const titleStyle = { color: c.ink, fontSize: isWide ? '32px' : '28px', lineHeight: 1.15, fontWeight: 800, letterSpacing: '-0.025em', margin: '0 0 8px', fontFamily: t.family };
  const subStyle = { color: c.inkMuted, fontSize: '15px', lineHeight: '22px', margin: 0, fontFamily: t.family };
  const primaryBtn = { width: '100%', height: '54px', background: loading ? c.brandTintEdge : c.brand, color: loading ? c.brandDeep : c.onBrand, border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: 700, fontFamily: t.family, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : '0 6px 18px rgba(22,163,74,0.35)', transition: 'background .18s ease, box-shadow .18s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' };

  const gate = signingIn ? (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(244,247,245,0.22)',
        backdropFilter: 'blur(10px) saturate(115%)',
        WebkitBackdropFilter: 'blur(10px) saturate(115%)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: t.family,
      }}
    >
      <div style={{ position: 'relative', width: 150, height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {[0, 0.8, 1.6].map((delay) => (
          <span
            key={delay}
            style={{
              position: 'absolute', width: 88, height: 88, borderRadius: '50%',
              border: `2px solid ${c.brand}`, opacity: 0,
              animation: 'pv-ripple 2.4s cubic-bezier(0.22, 0.61, 0.36, 1) infinite',
              animationDelay: `${delay}s`,
            }}
          />
        ))}
        <div
          style={{
            position: 'relative', width: 88, height: 88, borderRadius: '50%',
            background: c.surface, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 22px rgba(11,18,32,0.16)',
            animation: 'pv-breathe 2.4s ease-in-out infinite',
          }}
        >
          <img src={logo} alt="Pharvlink" style={{ width: 46, height: 46, objectFit: 'contain', display: 'block' }} />
        </div>
      </div>
      <p style={{ margin: '24px 0 0', fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: c.ink }}>
        Signing you in
      </p>
      <style>{`
        @keyframes pv-ripple {
          0%   { transform: scale(1);   opacity: 0.55; }
          70%  { opacity: 0.06; }
          100% { transform: scale(1.7); opacity: 0; }
        }
        @keyframes pv-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.045); }
        }
      `}</style>
    </div>
  ) : null;

  // What the platform actually does for a pharmacy, in their words rather than
  // ours. Three is the limit — a wall of claims reads as marketing filler.
  const points = [
    { k: 'Verified suppliers', v: 'Every vendor checked before they can list' },
    { k: 'Money held safely', v: 'Payment released only once you confirm delivery' },
    { k: 'Quotes, not guesswork', v: 'Compare real prices from multiple vendors' },
  ];

  const brandPanel = (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        // Weighted to the deep end on purpose: white on c.brand alone is 3.3:1,
        // under the readable floor for the body copy sitting on it. Every stop
        // here clears 4.5:1.
        background: `linear-gradient(150deg, #0E5C2B 0%, ${c.brandDeep} 52%, #158441 100%)`,
        padding: isWide ? '48px 44px' : '28px 24px 30px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isWide ? 'space-between' : 'flex-start',
        minHeight: isWide ? '600px' : 'auto',
      }}
    >
      {/* Two soft radial washes. Cheaper than an illustration, no bytes to
          download, and it stops the panel reading as a flat block of green. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background:
            'radial-gradient(520px 320px at 88% 8%, rgba(255,255,255,0.16), transparent 60%),' +
            'radial-gradient(420px 300px at 8% 92%, rgba(255,255,255,0.10), transparent 62%)',
        }}
      />

      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '42px', height: '42px', borderRadius: radius.md,
              background: '#FFFFFF', display: 'grid', placeItems: 'center', flexShrink: 0,
            }}
          >
            <img src={logo} alt="" style={{ width: '28px', height: '28px', objectFit: 'contain', display: 'block' }} />
          </span>
          <span style={{ color: '#FFFFFF', fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em', fontFamily: t.family }}>
            Pharvlink
          </span>
        </div>

        {isWide && (
          <>
            <h2 style={{ margin: '32px 0 12px', color: '#FFFFFF', fontSize: '29px', lineHeight: 1.2, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: t.family, maxWidth: '15ch' }}>
              Stock your pharmacy with confidence.
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.86)', fontSize: '15px', lineHeight: '23px', maxWidth: '34ch', fontFamily: t.family }}>
              One place to source from verified pharmaceutical vendors across Nigeria.
            </p>
          </>
        )}
      </div>

      {isWide && (
        <div style={{ position: 'relative', marginTop: '34px', display: 'grid', gap: '16px' }}>
          {points.map((p) => (
            <div key={p.k} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span
                aria-hidden="true"
                style={{
                  width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
                  background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.34)',
                  display: 'grid', placeItems: 'center', color: '#FFFFFF', fontSize: '11px', fontWeight: 800,
                }}
              >
                ✓
              </span>
              <div>
                <p style={{ margin: 0, color: '#FFFFFF', fontSize: '14px', fontWeight: 700, fontFamily: t.family }}>{p.k}</p>
                <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.78)', fontSize: '13px', lineHeight: '19px', fontFamily: t.family }}>{p.v}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const form = (
    <div style={{ padding: isWide ? '48px 44px' : '26px 24px 34px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
        <h1 style={titleStyle}>Welcome back</h1>
        <p style={{ ...subStyle, marginBottom: '28px' }}>Sign in to continue to your account</p>

        <AuthAlert error={formError} onDismiss={() => setFormError(null)} />

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required style={inputStyle} placeholder="your@email.com"
              onFocus={(e) => { e.target.style.borderColor = c.brand; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.12)'; }}
              onBlur={(e) => { e.target.style.borderColor = c.line; e.target.style.boxShadow = 'none'; }} />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Password</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange} required style={inputStyle} placeholder="Enter your password"
              onFocus={(e) => { e.target.style.borderColor = c.brand; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.12)'; }}
              onBlur={(e) => { e.target.style.borderColor = c.line; e.target.style.boxShadow = 'none'; }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: c.inkMuted, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: t.family, WebkitTapHighlightColor: 'transparent' }}>
              <input type="checkbox" checked={rememberMe} onChange={(e) => { setRememberMe(e.target.checked); localStorage.setItem('mv_remember_me_checked', e.target.checked ? '1' : ''); }} style={{ accentColor: c.brand, width: '18px', height: '18px', cursor: 'pointer' }} />
              Remember me
            </label>
            <Link to="/forgot-password" style={{ color: c.brandDeep, textDecoration: 'none', fontWeight: 600, fontSize: '13px', fontFamily: t.family }}>Forgot password?</Link>
          </div>

          <button type="submit" disabled={loading} style={primaryBtn}>{loading ? 'Signing in…' : 'Sign in'}</button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '26px', paddingTop: '20px', borderTop: '1px solid ' + c.line }}>
          <p style={{ color: c.inkMuted, fontSize: '14px', margin: 0, fontFamily: t.family }}>
            Don't have an account? <Link to="/register" style={{ color: c.brandDeep, textDecoration: 'none', fontWeight: 700 }}>Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: isWide ? '40px 32px' : 0,
        fontFamily: t.family,
        color: c.inkBody,
        position: 'relative',
        overflow: 'hidden',
        // Mint wash rather than the flat canvas, so the white card reads as
        // floating on something rather than sitting on more of the same. Kept
        // in the brand's own green family — light enough that the card still
        // has the strongest contrast on the page.
        background: '#DCFCE7',
      }}
    >
      {/* Photograph, blurred. The file is only 8–12KB because blur destroys
          detail anyway — there is no point shipping a sharp image to smudge it.
          Scaled up 1.15x so the blur's soft edges fall outside the viewport
          instead of showing as a pale frame. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `url(${isWide ? bgDesktop : bgMobile})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(14px)',
          transform: 'scale(1.15)',
          opacity: 1,
        }}
      />
      {/* Green wash over the photo. Without this the pill colours fight the
          form for attention and the text loses contrast — the point is a
          suggestion of pharmacy, not a picture of one. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(135deg, rgba(240,253,244,0.22) 0%, rgba(220,252,231,0.18) 38%, rgba(167,243,208,0.20) 72%, rgba(110,231,183,0.24) 100%)',
        }}
      />
      {/* Two diagonal sheens. This is what gives it the polish — a flat tint
          looks like a mistake, a moving highlight looks deliberate. Pure CSS,
          no image, and pointer-events off so it never eats a tap. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background:
            'linear-gradient(118deg, transparent 32%, rgba(255,255,255,0.16) 46%, rgba(255,255,255,0.05) 58%, transparent 70%),' +
            'radial-gradient(760px 520px at 84% 6%, rgba(255,255,255,0.16), transparent 62%),' +
            'radial-gradient(620px 460px at 6% 96%, rgba(22,163,74,0.14), transparent 64%)',
        }}
      />
      {gate}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: isWide ? '980px' : '100%',
          margin: '0 auto',
          background: c.surface,
          border: isWide ? '1px solid ' + c.line : 'none',
          borderRadius: isWide ? '22px' : 0,
          boxShadow: isWide ? '0 24px 60px rgba(6,78,59,0.18), 0 2px 8px rgba(6,78,59,0.06)' : 'none',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: isWide ? '1fr 1fr' : '1fr',
          // No fixed height: the third value prop was being clipped by the
          // card's overflow:hidden when the panel content ran past 560px.
          minHeight: isWide ? 'auto' : '100dvh',
        }}
      >
        {brandPanel}
        {form}
      </div>
    </div>
  );
};

export default Login;